/**
 * Sentry Error Monitoring - Frontend Initialization
 *
 * Captures unhandled errors, performance traces, and session replays.
 * PII protection: all text masked, all media blocked in replays.
 */

import * as Sentry from '@sentry/react';

let initialized = false;

export function initSentry(): void {
  if (initialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn('[Sentry] VITE_SENTRY_DSN not set - error monitoring disabled');
    return;
  }

  Sentry.init({
    dsn,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    environment: import.meta.env.MODE, // 'production' | 'development'
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Scope trace propagation to our own backends only
    tracePropagationTargets: [
      /^https:\/\/us-central1-corbel-ai-pipeline\.cloudfunctions\.net/,
      /^https:\/\/.*\.cloudfunctions\.net/,
      /^https:\/\/.*\.run\.app/,
    ],

    // Sample rates
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filter noisy errors that aren't actionable
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value || '';

      // ResizeObserver loop is a browser quirk, not a real error
      if (message.includes('ResizeObserver loop')) return null;

      // Network errors from ad blockers / extensions
      if (message === 'NetworkError when attempting to fetch resource.') return null;

      // Firebase auth "popup closed by user" is user-initiated
      if (message.includes('auth/popup-closed-by-user')) return null;

      return event;
    },
  });

  initialized = true;
}
